import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { useDomainStore } from "./domain";
import { useInstanceWorkspacesStore } from "./i-workspace";
import router from "../router";

const BOT_STATES = ["STANDBY", "START", "LISTENING", "PREPARING", "FREESTYLE"] as const;
type STATE = (typeof BOT_STATES)[number];

interface SSEPayload {
	state: STATE;
	text: string;
}

type TSetAttributePayload = {
	toolType: "set_attribute_value";
	attributeId: string;
	value: string;
	created?: number;
};
type TMatchCityPayload = {
	toolType: "match_city";
	cityQuery: string;
	created?: number;
};
type TMatchStreetPayload = {
	toolType: "match_street";
	streetQuery: string;
	city: string;
	limit?: number;
	created?: number;
};
type TMatchPoiPayload = {
	toolType: "match_poi";
	poiQuery: string;
	city: string;
	limit?: number;
	created?: number;
};
type TMatchPoiHousenumberPayload = {
	toolType: "match_poi_housenumber";
	street: string;
	housenumber: string;
	city: string;
	limit?: number;
	created?: number;
};
type TGetPoisForCityPayload = {
	toolType: "get_pois_for_city";
	city: string;
	placeTypes: string[];
	created?: number;
};
type TEndSessionPayload = {
	toolType: "end_session";
	created?: number;
};
type TConfirmSuggestionPayload = {
	toolType: "confirm_suggestion";
	attrName: string;
	value: string;
	created?: number;
};

export type ToolCallEvent = {
	id: string;
	receivedAt: number;
	payload:
		| TSetAttributePayload
		| TMatchCityPayload
		| TMatchStreetPayload
		| TMatchPoiPayload
		| TMatchPoiHousenumberPayload
		| TGetPoisForCityPayload
		| TEndSessionPayload
		| TConfirmSuggestionPayload;
};

let domainStore: ReturnType<typeof useDomainStore>;
let workspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
function init() {
	if (!domainStore) domainStore = useDomainStore();
	if (!workspacesStore) workspacesStore = useInstanceWorkspacesStore();
}

export const useVoiceBotStore = defineStore("voice_bot", () => {
	const workspaceId = computed(() => {
		return router.currentRoute.value.params.workspaceId as string;
	});

	const eventSource = ref<EventSource | null>(null);
	const currentState = ref<STATE>("STANDBY");
	const toolCallEvents = ref<ToolCallEvent[]>([]);

	function initSSE() {
		init();
		const domainId = domainStore.domain?.id;
		const workspaceId = workspacesStore.openedWorkspaceId;
		if (!domainId || !workspaceId) return;

		let endpoint = window.location.href.includes("localhost:3000") ? import.meta.env.VITE_API_HOST || "" : "";
		endpoint += "/sseVoiceBot";

		const url = new URL(endpoint, window.location.href);
		url.searchParams.set("domainId", String(domainId));
		url.searchParams.set("workspaceId", String(workspaceId));

		closeSSE();

		eventSource.value = new EventSource(url.toString());
		eventSource.value.onmessage = onMessage;
		eventSource.value.onerror = () => {
			console.warn("[BotSSEMessage] SSE error");
			currentState.value = "STANDBY";
		};

		eventSource.value.addEventListener("voicebotError", (rawEvent: Event) => {
			try {
				const data = JSON.parse((rawEvent as MessageEvent).data);
				if (data?.code === 404) {
					closeSSE();
					currentState.value = "STANDBY";
				}
			} catch (err) {
				console.warn("[BotSSEMessage] voicebotError parse failed", err);
			}
		});
	}

	function closeSSE() {
		if (eventSource.value) {
			eventSource.value.close();
			eventSource.value = null;
		}
	}

	function onMessage(event: MessageEvent) {
		let parsed: any;
		try {
			parsed = JSON.parse(event.data);
		} catch (err) {
			console.warn("[VoiceBotStore] Invalid SSE payload", err);
			return;
		}

		// Try to parse text field if it's a non-empty string
		if (typeof parsed.text === "string" && parsed.text.length > 0) {
			try {
				const parsedText = JSON.parse(parsed.text);
				if (isToolCallPayload(parsedText)) {
					recordToolCall(parsedText);
					return;
				}
			} catch {
				// text is not JSON, treat as regular state payload
			}
		}

		handlePayload(parsed as SSEPayload);
	}

	function isToolCallPayload(
		value: unknown,
	): value is
		| TSetAttributePayload
		| TMatchCityPayload
		| TMatchStreetPayload
		| TMatchPoiPayload
		| TMatchPoiHousenumberPayload
		| TGetPoisForCityPayload
		| TEndSessionPayload {
		return !!value && typeof value === "object" && "toolType" in value;
	}

	function handlePayload(payload: SSEPayload) {
		if (!isValidState(payload.state)) return;

		if (payload.text === "CLOSE") {
			currentState.value = "STANDBY";
			closeSSE();
			return;
		}

		currentState.value = payload.state;
	}

	function isValidState(value: unknown): value is STATE {
		return typeof value === "string" && BOT_STATES.includes(value as STATE);
	}

	function recordToolCall(
		payload:
			| TSetAttributePayload
			| TMatchCityPayload
			| TMatchStreetPayload
			| TMatchPoiPayload
			| TMatchPoiHousenumberPayload
			| TGetPoisForCityPayload
			| TEndSessionPayload,
	) {
		const receivedAt =
			typeof payload.created === "number" && !Number.isNaN(payload.created) ? payload.created * 1000 : Date.now();
		toolCallEvents.value = [
			...toolCallEvents.value.slice(-49),
			{
				id: `${receivedAt}-${Math.random().toString(16).slice(2)}`,
				receivedAt,
				payload,
			},
		];
	}

	watch(
		() => workspaceId.value,
		() => {
			toolCallEvents.value = [];
		},
		{ immediate: true },
	);

	return {
		initSSE,
		closeSSE,
		currentState,
		toolCallEvents,
	};
});
